// src/components/authentication/SignIn.tsx

// @ts-ignore
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../redux/store";
import { useForm, SubmitHandler } from "react-hook-form";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import home_img from "../../../static/images/common/hero-systel.png";

import {
    User,
    UtenteLogged,
    utenteTokenObtain,
    resetErrorStatus,
} from "./authenticationSlice";
import { ErrorStatusTypes } from "../common/errorsDeclarations";
import { UserStored, updateRememberedUsername } from "../userPreference/userPreferenceSlice";

import {
    Typography,
    Link,
    Grid,
    Box,
    Container,
    FormControlLabel,
    CircularProgress,
    Avatar,
    Button,
    CssBaseline,
    TextField,
    Checkbox,
    Backdrop,
    Alert,
    Chip,
} from "@mui/material";

import logo from "../../../static/images/logo/claire_biglia2.png";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import CardContent from "@mui/material/CardContent";

interface SignInFormData extends User {
    rememberUsername: boolean;
}

const SignIn = ({ ...props }) => {
    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const passwordRef = useRef<HTMLInputElement>(null);
    const usernameRef = useRef<HTMLInputElement>(null);

    const utente: UtenteLogged = useSelector<RootState, UtenteLogged>(
        (state) => state.authentication.utenteLogged
    );

    const userStored: UserStored | undefined = useSelector<RootState, UserStored | undefined>(
        (state) => state.userPreferenceState.userStored
    );

    const [saving, setSaving] = useState(false);
    const [showRememberedUser, setShowRememberedUser] = useState(false);
    // ✅ NUOVO: stato per gestire redirect dopo login
    const [hasLoggedIn, setHasLoggedIn] = useState(false);

    type LocationState = {
        from: {
            pathname: string;
        };
    };
    const location = useLocation();
    const { from } = (location.state as LocationState) || {
        from: { pathname: "/" },
    };

    const {
        handleSubmit,
        register,
        formState: { errors },
        watch,
        setValue,
        setError,
        reset,
        clearErrors,
    } = useForm<SignInFormData>({
        defaultValues: {
            username: "",
            password: "",
            rememberUsername: false,
        }
    });

    // Gestione username remembered all'avvio
    useEffect(() => {
        if (userStored?.rememberUsername && userStored?.username) {
            setValue("username", userStored.username);
            setValue("rememberUsername", true);
            setShowRememberedUser(true);

            setTimeout(() => {
                passwordRef.current?.focus();
            }, 100);
        } else {
            setTimeout(() => {
                usernameRef.current?.focus();
            }, 100);
        }
    }, [setValue, userStored]);

    const clearRememberedUser = () => {
        setValue("username", "");
        setValue("rememberUsername", false);
        setShowRememberedUser(false);
        dispatch(updateRememberedUsername({ username: "", remember: false }));
        clearErrors("username");

        setTimeout(() => {
            usernameRef.current?.focus();
        }, 100);
    };

    const useRememberedUser = () => {
        if (userStored?.username) {
            setValue("username", userStored.username);
            setValue("rememberUsername", true);
            clearErrors("username");

            setTimeout(() => {
                passwordRef.current?.focus();
            }, 100);
        }
    };

    useEffect(() => {
        utente?.errorsStack?.fieldsErrors?.username &&
        setError("username", {
            type: "server",
            message: utente.errorsStack.fieldsErrors.username.toString(),
        });
        utente?.errorsStack?.fieldsErrors?.password &&
        setError("password", {
            type: "server",
            message: utente.errorsStack.fieldsErrors.password.toString(),
        });
    }, [utente?.errorsStack?.fieldsErrors]);

    const onSubmit: SubmitHandler<SignInFormData> = (data) => {
        setSaving(true);
        setHasLoggedIn(true); // ✅ NUOVO: marca che abbiamo tentato login

        const user: User = {
            username: data.username,
            password: data.password,
        };

        dispatch(utenteTokenObtain({
            user,
            rememberUsername: data.rememberUsername
        }));
    };

    // ✅ MIGLIORATO: Gestione redirect dopo login con retry logic
    useEffect(() => {
        if (utente?.errorsStack?.status === ErrorStatusTypes.OK && utente.isLogged && hasLoggedIn) {
            console.log('Login successful, navigating to:', from.pathname);

            // Reset error status
            dispatch(resetErrorStatus());

            // ✅ NUOVO: Piccolo delay per assicurarsi che tutto sia inizializzato
            setTimeout(() => {
                const destinazione = from.pathname === '/me/changepassword/' ? '/' : from.pathname;
                navigate(destinazione, { replace: true });
            }, 100);

        } else if (utente?.errorsStack?.status === ErrorStatusTypes.ERROR && hasLoggedIn) {
            // Login fallito
            setSaving(false);
            setHasLoggedIn(false);
        }
    }, [utente?.errorsStack?.status, utente.isLogged, hasLoggedIn, from.pathname, navigate, dispatch]);

    // ✅ NUOVO: Controllo se utente è già loggato (per evitare doppi render)
    if (utente.isLogged && !hasLoggedIn) {
        // Utente già loggato da sessione ripristinata, redirect diretto
        console.log('User already logged in, redirecting to:', from.pathname);
        return <Navigate to={from.pathname === '/me/changepassword/' ? '/' : from.pathname} replace />;
    }

    return (
        <Box>
            <Backdrop
                sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
                open={saving}
            >
                <CircularProgress color="inherit" />
            </Backdrop>
            <Grid
                container
                direction="row"
                sx={{justifyContent: "space-evenly", alignItems: "center", mt:3}}
                spacing={2}>
                <Grid size={{xs: 12, md: 6, lg:3, }} >
                    <Box
                        sx={{
                            mt: 6,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                        }}
                    >
                        <Avatar sx={{ m: 1, bgcolor: "secondary.main" }}>
                            <img
                                src={logo}
                                style={{ width: "100%", height: "100%", objectFit: "contain" }}
                                alt="logo"
                            />
                        </Avatar>
                        <Typography component="h1" variant="h5">
                            Sign in
                        </Typography>

                        {/* ✅ NUOVO: Info sulla destinazione */}
                        {from.pathname !== "/" && (
                            <Alert severity="info" sx={{ mt: 2, width: "100%" }}>
                                Accedi per continuare verso: <strong>{from.pathname}</strong>
                            </Alert>
                        )}

                        {showRememberedUser && userStored?.username && (
                            <Box sx={{ mt: 2, mb: 1, width: "100%" }}>
                                <Alert
                                    severity="info"
                                    sx={{ alignItems: "center" }}
                                    action={
                                        <Box sx={{ display: "flex", gap: 1 }}>
                                            <Chip
                                                label="Usa"
                                                size="small"
                                                color="primary"
                                                variant="outlined"
                                                onClick={useRememberedUser}
                                                clickable
                                            />
                                            <Chip
                                                label="Rimuovi"
                                                size="small"
                                                variant="outlined"
                                                onClick={clearRememberedUser}
                                                clickable
                                            />
                                        </Box>
                                    }
                                >
                                    Utente memorizzato: <strong>{userStored.username}</strong>
                                </Alert>
                            </Box>
                        )}

                        <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 1 }}>
                            <TextField
                                inputRef={usernameRef}
                                margin="normal"
                                required
                                fullWidth
                                id="username"
                                label="Username"
                                autoComplete="username"
                                autoFocus={!showRememberedUser}
                                error={!!errors?.username}
                                helperText={errors?.username?.message}
                                disabled={saving} // ✅ NUOVO: disabilita durante saving
                                {...register("username", {
                                    required: "Username è richiesto",
                                    minLength: {
                                        value: 3,
                                        message: "Username deve essere almeno 3 caratteri",
                                    },
                                })}
                            />
                            <TextField
                                inputRef={passwordRef}
                                margin="normal"
                                required
                                fullWidth
                                label="Password"
                                type="password"
                                id="password"
                                autoComplete="current-password"
                                error={!!errors?.password}
                                helperText={errors?.password?.message}
                                disabled={saving} // ✅ NUOVO: disabilita durante saving
                                {...register("password", {
                                    required: "Password è richiesta",
                                    minLength: {
                                        value: 6,
                                        message: "Password deve essere almeno 6 caratteri",
                                    },
                                })}
                            />
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        {...register("rememberUsername")}
                                        color="primary"
                                        disabled={saving} // ✅ NUOVO
                                    />
                                }
                                label="Ricorda username"
                            />
                            <Button
                                type="submit"
                                fullWidth
                                variant="contained"
                                sx={{ mt: 3, mb: 2 }}
                                disabled={saving}
                            >
                                {saving ? "Accesso in corso..." : "Sign In"}
                            </Button>
                            <Grid container>
                                <Grid  >
                                    <Link href="/forgotPassword" variant="body2">
                                        Forgot password?
                                    </Link>
                                </Grid>
                                <Grid >
                                    {/* Link registrazione se necessario */}
                                </Grid>
                            </Grid>
                        </Box>
                    </Box>
                </Grid>
                <Grid size={{xs: 12, md: 6}}>
                    <Card raised>
                        <CardMedia
                            component="img"
                            image={home_img}
                            // height="430"
                            sx={{
                                objectFit: "cover",
                                // objectPosition: "50% 0%",
                            }}
                            alt="Azienda Evop"
                        />
                        <CardContent>
                            <Typography gutterBottom variant="h4">
                                Claire - Pianificazione della Produzione
                            </Typography>
                            <Typography gutterBottom>
                                Systel Elettronica
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>



        </Box>
    );
};

export default SignIn;
